import React, { Fragment, useMemo, useRef } from "react";
import {
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  Handshake,
  Mail,
  Phone,
  MapPin,
  Building2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import SevakCardsSlider from "../../components/virtual/SevakCardsSlider";
import VirtualFooter from "../../components/virtual/VirtualFooter";

const toSlug = (str = "") =>
  str
    .toString()
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const SevakDetail = () => {
  const { module, slug } = useParams(); // ✅ module + slug
  const navigate = useNavigate();

  const {
    data: listRes,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["list_sevak", module],
    queryFn: async () => {
      const res = await axios.get(
        `https://virtualsevak.com/api/v1/public/${module}`,
      );
      return res.data; // { success, data: [...] }
    },
    enabled: !!module,
    staleTime: 1000 * 60 * 30,
  });
  const listArr = useMemo(() => {
    return Array.isArray(listRes?.data) ? listRes.data : [];
  }, [listRes]);

  const item = useMemo(() => {
    if (!slug) return null;
    const idFromSlug = slug.split("-").pop();
    return listArr.find((x) => String(x.id) === String(idFromSlug)) || null;
  }, [listArr, slug]);

  // ✅ prev/next
  const currentIndex = useMemo(() => {
    if (!slug) return -1;
    const idFromSlug = slug.split("-").pop();
    return listArr.findIndex((x) => String(x.id) === String(idFromSlug));
  }, [listArr, slug]);

  const prevItem = useMemo(() => {
    if (currentIndex <= 0) return null;
    return listArr[currentIndex - 1] || null;
  }, [listArr, currentIndex]);

  const nextItem = useMemo(() => {
    if (currentIndex < 0 || currentIndex >= listArr.length - 1) return null;
    return listArr[currentIndex + 1] || null;
  }, [listArr, currentIndex]);

  const toRoute = (x) =>
    `/virtual-sevak/${module}/${toSlug(x?.site_name || x?.name || "item")}-${x?.id}`;

  const widgets = item?.hero_widgets || [];

  const fullHeaderImage = widgets.filter(
    (w) => w.template === "full-header-image",
  );
  const headerText = widgets.filter((w) => w.template === "header-text");
  const headerAndText = widgets.filter((w) => w.template === "header-and-text");

  const rowList = React.useMemo(() => {
    const grouped = (item?.carousel_sliders || [])
      ?.filter((s) => Number(s?.row_alignment) !== 3)
      ?.reduce((acc, s) => {
        const key = Number(s?.row_alignment || 1);
        if (!acc[key]) acc[key] = [];
        acc[key].push({
          image: s?.sliderimage,
          href: s?.image_link || s?.caption1_link || "#",
          ctas: [
            s?.caption1
              ? {
                  label: s.caption1,
                  href: s?.caption1_link || s?.image_link || "#",
                }
              : null,
            s?.caption2
              ? {
                  label: s.caption2,
                  href: s?.caption2_link || s?.image_link || "#",
                }
              : null,
          ].filter(Boolean),
        });
        return acc;
      }, {});

    return Object.entries(grouped)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([id, items]) => ({ id: Number(id), items }));
  }, [item?.carousel_sliders]);

  if (isPending) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-gray-600">
        Loading...
      </div>
    );
  }

  if (isError || !listRes?.success) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-gray-600">
        Something went wrong.
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-3xl px-4 py-10">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-full border border-orange-100 bg-white px-3 py-2 text-sm font-semibold text-orange-900"
          >
            <ArrowLeft size={16} /> Back
          </button>

          <div className="mt-6 rounded-2xl border border-orange-100 bg-orange-50 p-5">
            <h1 className="text-lg font-extrabold text-orange-950">
              Not Found
            </h1>
            <p className="mt-1 text-sm text-gray-700">
              No record found for slug:{" "}
              <span className="font-semibold">{slug}</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ map your API fields here
  const heroImage = item?.banner_image;
  const title = item?.name;
  const description = item?.about;
  const address = [item?.city_name, item?.state_name]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-orange-50/60 via-white to-white">
        <div className="mx-auto  max-w-[1380px] px-2 sm:px-4 py-4">
          {/* Top bar */}

          <div className="px-4 py-2 flex flex-wrap items-center gap-3 sm:flex-nowrap justify-between">
            <button
              onClick={() => navigate(`/virtual-sevak/${module}`)}
              className="order-1 sm:order-none group inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900"
            >
              <ArrowLeft
                size={16}
                className="transition-transform group-hover:-translate-x-1"
              />
              Back to list
            </button>

            <div className="order-3 w-full sm:order-none sm:w-auto flex justify-center gap-3">
              <button
                disabled={!prevItem}
                title={
                  prevItem
                    ? prevItem?.site_name || prevItem?.name
                    : "No previous"
                }
                onClick={() => prevItem && navigate(toRoute(prevItem))}
                className="group flex items-center gap-2 px-4 py-2 rounded-xl
        border border-gray-200 bg-white
        hover:border-orange-400 hover:bg-orange-50
        disabled:opacity-40"
              >
                <ChevronLeft
                  size={16}
                  className="transition-transform group-hover:-translate-x-1"
                />
                <span className="text-sm font-semibold">Prev</span>
              </button>

              <button
                disabled={!nextItem}
                onClick={() => nextItem && navigate(toRoute(nextItem))}
                className="group flex items-center gap-2 px-4 py-2 rounded-xl
        bg-orange-500 text-white
        hover:bg-orange-600
        disabled:opacity-40"
                title={
                  nextItem ? nextItem?.site_name || nextItem?.name : "No next"
                }
              >
                <span className="text-sm font-semibold">Next</span>
                <ChevronRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </button>
            </div>

            {item?.subdomain && (
              <a
                href={item.subdomain}
                target="_blank"
                rel="noopener noreferrer"
                className="
        order-2 sm:order-none
        inline-flex items-center gap-1.5
        px-3 py-1.5 rounded-md
        bg-gray-100 text-gray-700
        font-medium text-xs sm:text-sm
        hover:bg-gray-200 transition-colors
      "
              >
                Visit on Site <span>↗</span>
              </a>
            )}
          </div>

          <section className="mt-1 overflow-hidden rounded-2xl ">
            <div className="bg-[#E07436] px-4 py-4">
              <div className="grid grid-cols-12 items-start gap-4">
                {/* Org Logo */}
                <div className="col-span-12 sm:col-span-6 lg:col-span-3  flex justify-center sm:justify-start">
                  {item?.org_logo && (
                    <img
                      src={item.org_logo}
                      alt="org logo"
                      className="h-16 w-auto sm:h-24 sm:w-40 object-contain sm:object-fill"
                    />
                  )}
                </div>

                {/* Profile + Title */}
                <div className="col-span-12 sm:col-span-6 lg:col-span-5  xl:col-span-6  flex items-center gap-4">
                  {item?.picture && (
                    <img
                      src={item.picture}
                      alt={title}
                      className="h-16 w-auto sm:h-24 sm:w-24 object-contain sm:object-fill rounded-full"
                    />
                  )}

                  <div>
                    {/* Title + Labels */}
                    <div className="flex flex-wrap items-center gap-3">
                      <h1 className="text-xl sm:text-2xl font-bold text-white capitalize">
                        {title}
                      </h1>
                    </div>

                    {/* Description */}
                    {!!description && (
                      <p
                        className="text-xs sm:text-sm text-white/90 line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: description }}
                      />
                    )}
                  </div>
                </div>
                {/* Organization + Channel Partner */}
                {/* <div className="col-span-12 sm:col-span-6 lg:col-span-2  lg:space-y-2 flex items-center gap-3 lg:block">
                  {item?.organization_name && (
                    <div>
                      <div className="flex items-center gap-1 text-[13px] font-semibold text-[#AA1125]">
                        <Building2 size={14} />
                        <span>Organization</span>
                      </div>

                      <p className="text-[13px] font-medium text-white capitalize ml-4">
                        {item.organization_name}
                      </p>
                    </div>
                  )}

                  {item?.channel_partner && (
                    <div>
                      <div className="flex items-center gap-1 text-[13px] font-semibold text-[#AA1125]">
                        <Handshake size={14} />
                        <span>Channel Partner</span>
                      </div>

                      <p className="text-[13px] font-medium text-white capitalize ml-4">
                        {item.channel_partner}
                      </p>
                    </div>
                  )}
                </div> */}

                {/* Contact Info */}
                <div className="col-span-12 sm:col-span-6 lg:col-span-2  flex flex-wrap md:flex-col gap-2 md:gap-1 text-sm text-white -ml-2">
                  {item?.organization_name && (
                    <div className="flex items-center gap-2">
                      <Building2
                        size={14}
                        className="font-semibold text-[#AA1125]"
                      />
                      <p className="text-[13px] font-medium text-white capitalize">
                        {item.organization_name}
                      </p>
                    </div>
                  )}
                  {item?.channel_partner && (
                    <div className="flex items-center gap-2">
                      <Handshake
                        size={14}
                        className="font-semibold text-[#AA1125]"
                      />
                      <p className="text-[13px] font-medium text-white capitalize">
                        {item.channel_partner}
                      </p>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Mail size={14} />
                    {item?.email ? (
                      <a href={`mailto:${item.email}`} className="truncate">
                        {item.email}
                      </a>
                    ) : (
                      "—"
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Phone size={14} />
                    {item?.telephone ? (
                      <a href={`tel:${item.telephone}`}>{item.telephone}</a>
                    ) : (
                      "—"
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <MapPin size={14} />
                    <span className="line-clamp-2">{address}</span>
                  </div>
                </div>
                <div className="col-span-12 sm:col-span-6 lg:col-span-2 xl:col-span-1  flex gap-3 lg:flex-col flex-wrap lg:justify-center lg:items-center sm:mt-0">
                  {/* CTA Buttons */}
                  <a
                    href={`${item?.subdomain}/login`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-[90px] px-4 py-2 text-center text-[14px] border border-white text-white font-semibold rounded-md hover:bg-white/10 transition"
                  >
                    Login
                  </a>

                  <a
                    href={`${item?.subdomain}/register`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-[90px] px-4 py-2 text-center text-[14px] bg-white text-orange-600 font-semibold rounded-md border border-white hover:bg-orange-50 transition"
                  >
                    Register
                  </a>
                </div>
              </div>
            </div>
            {/* Hero */}
            <div className="relative">
              <div className="h-[200px] sm:h-[500px] bg-orange-50">
                <img
                  src={heroImage}
                  alt={title}
                  className="h-full w-full object-fill"
                  loading="lazy"
                />
              </div>
              {item?.about_us?.content && (
                <div className="mt-4 ">
                  <h2 className="text-[15px] sm:text-[18px] font-extrabold text-orange-950">
                    {item?.about_us?.heading || "About Us"}
                  </h2>

                  <div
                    className="
        mt-3 text-[13px] sm:text-[14px] leading-relaxed text-gray-700
        prose prose-sm sm:prose-base max-w-none
        prose-p:my-2
        prose-strong:text-orange-900
        prose-em:text-orange-900/90
        prose-a:text-orange-600
        prose-headings:text-orange-950
      "
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(item.about_us.content),
                    }}
                  />
                </div>
              )}
              <div className="mt-6 space-y-6">
                {/* full-header-image */}
                {fullHeaderImage.map((w, i) => (
                  <FullHeaderImage key={`f-${i}`} w={w} />
                ))}

                {/* header-text */}
                {headerText.map((w, i) => (
                  <HeaderText key={`h-${i}`} w={w} />
                ))}

                {/* header-and-text (3 columns grouped) */}
                {headerAndText.length > 0 && (
                  <HeaderAndTextGrid
                    items={headerAndText}
                    title="Community Highlights"
                  />
                )}
              </div>

              {rowList.map((row) => (
                <RowSwiper key={row.id} row={row} />
              ))}
              <SevakCardsSlider data={item?.user_cards} logo={item?.org_logo} />
            </div>
          </section>
        </div>
      </div>
      <VirtualFooter item={item}/>
    
    </>
  );
};
const FullHeaderImage = ({ w }) => {
  const safeHTML = (html) => DOMPurify.sanitize(html || "");

  return (
    <div className="rounded-3xl overflow-hidden border border-orange-100/70 bg-white shadow-sm">
      {/* Title Section FIRST */}
      <div className="px-4 sm:px-6 pt-5 pb-3">
        <h2 className="text-[20px] sm:text-[30px] text-center font-extrabold tracking-tight text-orange-950 leading-snug">
          {w?.title}
        </h2>
        {/* <div className="mt-2 h-[3px] w-16 rounded-full bg-gradient-to-r from-orange-500 to-amber-400" /> */}
      </div>

      {/* Image */}
      <div className="relative  bg-orange-50">
        <img
          src={w?.upload_image}
          alt={w?.title || "hero"}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>

      {/* Description */}
      {(w?.description1 || w?.description2) && (
        <div className="p-4 sm:p-6">
          <div
            className="prose prose-sm sm:prose-base max-w-none text-gray-700
                       prose-strong:text-orange-900 prose-a:text-orange-600"
            dangerouslySetInnerHTML={{
              __html: safeHTML(
                (w?.description1 || "") + (w?.description2 || ""),
              ),
            }}
          />
        </div>
      )}
    </div>
  );
};

const HeaderText = ({ w }) => {
  const left = (w?.alignment || "left").toLowerCase() === "left";

  const desc1 = DOMPurify.sanitize(w?.description1 || "");
  const desc2 = DOMPurify.sanitize(w?.description2 || "");

  const isContain = w?.template === "templates-3"; // if you still want this rule

  return (
    <div className="group rounded-3xl border border-orange-100/70 bg-white shadow-sm hover:shadow-lg transition grid grid-cols-1 md:grid-cols-2 overflow-hidden">
      {/* Image */}
      <div className={left ? "order-1" : "order-1 md:order-2"}>
        <div className="relative h-[260px] sm:h-[320px] md:h-full bg-orange-50">
          <img
            src={w?.upload_image}
            alt={w?.title || "hero"}
            className={[
              "h-full w-full",
              isContain ? "object-contain" : "object-cover",
            ].join(" ")}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/0 to-black/0 opacity-80" />
        </div>
      </div>

      {/* Content */}
      <div className={left ? "order-2" : "order-2 md:order-1"}>
        <div className="h-full p-4 sm:p-6 lg:p-8 bg-white">
          <div>
            <h2 className="text-[18px] sm:text-[24px] font-extrabold tracking-tight text-orange-950">
              {w?.title}
            </h2>
            <div className="mt-2 h-[3px] w-14 rounded-full bg-gradient-to-r from-orange-500 to-amber-400" />
          </div>

          {!!desc1 && (
            <div
              className="mt-3 text-[13px] sm:text-[14px] leading-relaxed text-gray-700
                         prose prose-sm sm:prose-base max-w-none
                         prose-headings:my-2 prose-headings:text-orange-950
                         prose-strong:text-orange-900 prose-a:text-orange-600"
              dangerouslySetInnerHTML={{ __html: desc1 }}
            />
          )}

          {!!desc2 && (
            <div
              className="mt-3 text-[13px] sm:text-[14px] leading-relaxed text-gray-700
                         prose prose-sm sm:prose-base max-w-none"
              dangerouslySetInnerHTML={{ __html: desc2 }}
            />
          )}

          {w?.img_link ? (
            <div className="mt-5">
              <a
                href={w.img_link}
                target="_blank"
                rel="noreferrer"
                className="text-[12px] font-semibold text-orange-700 opacity-80 group-hover:opacity-100 hover:underline"
              >
                Open link →
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
const HeaderAndTextGrid = ({ items, title = "Highlights" }) => (
  <div className="rounded-3xl border border-orange-100/70 bg-white shadow-sm p-4 sm:p-6">
    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {items.map((w, i) => (
        <a
          key={i}
          href={w?.img_link || "#"}
          target="_blank"
          rel="noreferrer"
          className="group rounded-2xl overflow-hidden border border-orange-100 bg-white hover:shadow-md transition"
        >
          {/* Image */}
          <div className="relative  bg-orange-50 overflow-hidden">
            <img
              src={w?.upload_image}
              alt={w?.title || "card"}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
              loading="lazy"
            />
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Title below image */}
            <h3 className="text-[18px] font-extrabold text-orange-950 leading-snug">
              {w?.title}
            </h3>

            <div className="mt-2 h-[3px] w-10 rounded-full bg-gradient-to-r from-orange-500 to-amber-400" />

            {/* Description */}
            {(w?.description1 || w?.description2) && (
              <div
                className="mt-3 !text-[16px] leading-relaxed text-gray-700
                           prose prose-sm max-w-none
                           prose-strong:text-orange-900 prose-a:text-orange-600"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    (w?.description1 || "") + (w?.description2 || ""),
                  ),
                }}
              />
            )}
          </div>
        </a>
      ))}
    </div>
  </div>
);

const RowSwiper = ({ row }) => {
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  return (
    <section className="mt-6">
      <div className="relative rounded-2xl overflow-hidden">
        {/* Navigation Buttons */}
        <button
          ref={prevRef}
          className="
            absolute left-2 top-1/2 z-10 -translate-y-1/2
            h-9 w-9 flex items-center justify-center
            rounded-full bg-white shadow-md
            border border-orange-200
            text-orange-700
            hover:bg-orange-50
            transition
          "
        >
          <ChevronLeft size={18} />
        </button>

        <button
          ref={nextRef}
          className="
            absolute right-2 top-1/2 z-10 -translate-y-1/2
            h-9 w-9 flex items-center justify-center
            rounded-full bg-white shadow-md
            border border-orange-200
            text-orange-700
            hover:bg-orange-50
            transition
          "
        >
          <ChevronRight size={18} />
        </button>

        <Swiper
          modules={[Navigation]}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
          }}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          loop={row.items.length > 1}
          spaceBetween={14}
          slidesPerView={1.1}
          breakpoints={{
            640: { slidesPerView: 1.2 },
            768: { slidesPerView: 2.2 },
            1024: { slidesPerView: 3.2 },
          }}
          className="!p-3 sm:!p-4"
        >
          {row.items.map((it, idx) => (
            <SwiperSlide key={idx}>
              <a
                href={it.href}
                target="_blank"
                rel="noreferrer"
                className="group block rounded-2xl overflow-hidden border border-orange-100 bg-white hover:shadow-md transition"
              >
                <div className="relative">
                  <img
                    src={it.image}
                    alt={`slide-${idx}`}
                    className=" w-full object-fill"
                  />
                  {/* <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" /> */}
                </div>

                <div className="absolute bottom-3 left-3 flex gap-2">
                  {it.ctas.map((c, i) => (
                    <span
                      key={i}
                      className="
        bg-white/90 backdrop-blur-sm
        px-3 py-1
        text-[12px] font-semibold
        text-orange-900
        rounded-md shadow
      "
                    >
                      {c.label}
                    </span>
                  ))}
                </div>
              </a>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};
export default SevakDetail;
